import React from "react";
import { useLang, formatDate, formatTime, formatWeekday } from "@/i18n";
import { OrnateHeartDivider, GoldFlourish } from "./FloralOrnaments";
import GoogleMapsLogo from "./GoogleMapsLogo";
import { ChevronDown, Calendar } from "lucide-react";

export default function InvitationCard({ wedding, guestName }) {
    const { t, lang } = useLang();
    const hall = lang === "ar" ? wedding.hall_ar : wedding.hall_en;
    const scrollToRsvp = () => {
        document.querySelector('[data-testid="rsvp-section"]')?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <section
            data-testid="invitation-card"
            className="min-h-[100dvh] flex items-center justify-center px-4 py-14 wd-paper-texture relative"
        >
            <div className="relative w-full max-w-md mx-auto">
                <div className="wd-gold-frame bg-[var(--wd-bg)] rounded-lg overflow-hidden">
                    <div className="relative z-10 px-7 pt-14 pb-14 text-center">
                        <p className="wd-label text-[var(--wd-accent)]" data-testid="card-invitation-label">
                            {lang === "ar" ? "دعوة زفاف" : "Wedding Invitation"}
                        </p>

                        <p className="mt-6 text-[var(--wd-text-muted)] text-sm max-w-xs mx-auto leading-relaxed">
                            {t("areGettingMarried")}
                        </p>

                        <h2
                            className="wd-script text-[var(--wd-accent)] mt-6 text-[2.35rem] md:text-6xl leading-none drop-shadow-sm max-w-full"
                            data-testid="card-names"
                            translate="no"
                        >
                            {lang === "ar"
                                ? `${wedding.groom_ar} & ${wedding.bride_ar}`
                                : `${wedding.groom_en} & ${wedding.bride_en}`}
                        </h2>

                        <div className="mt-8 flex justify-center"><OrnateHeartDivider width={220} /></div>

                        <div className="mt-6 space-y-1">
                            <div className="inline-flex items-center gap-2 text-[var(--wd-accent)]">
                                <Calendar size={14} />
                                <span className="wd-label">{lang === "ar" ? "الموعد" : "When"}</span>
                            </div>
                            <p className="wd-heading text-2xl md:text-3xl text-[var(--wd-primary)] mt-1">
                                {formatWeekday(wedding.date_iso, lang)}
                            </p>
                            <p className="wd-heading text-xl md:text-2xl text-[var(--wd-primary)]">
                                {formatDate(wedding.date_iso, lang)}
                            </p>
                            <p className="text-[var(--wd-text-muted)] text-sm mt-1">
                                {t("at")} <span className="text-[var(--wd-primary)] font-medium">{formatTime(wedding.date_iso, lang)}</span>
                            </p>
                            <p className="text-[var(--wd-rose-deep)] text-xs italic mt-2">
                                {lang === "ar" ? wedding.note_ar : wedding.note_en}
                            </p>
                        </div>

                        <div className="mt-6 flex justify-center"><GoldFlourish /></div>

                        <div className="mt-5 space-y-1">
                            <a
                                href={wedding.maps_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 text-[var(--wd-accent)] hover:text-[var(--wd-primary)] transition-colors"
                                data-testid="card-place-label-link"
                            >
                                <GoogleMapsLogo size={16} />
                                <span className="wd-label">{lang === "ar" ? "المكان" : "Where"}</span>
                            </a>
                            <a
                                href={wedding.maps_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="wd-heading block text-xl md:text-2xl text-[var(--wd-primary)] mt-1 hover:text-[var(--wd-accent)] transition-colors"
                                data-testid="card-venue-link"
                            >
                                {lang === "ar" ? wedding.venue_ar : wedding.venue_en}
                            </a>
                            {hall ? (
                                <p className="wd-heading text-lg md:text-xl text-[var(--wd-primary)]" data-testid="card-hall">
                                    {hall}
                                </p>
                            ) : null}
                            <p className="text-[var(--wd-text-muted)] text-sm mt-1">
                                {lang === "ar" ? wedding.city_ar : wedding.city_en}
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={scrollToRsvp}
                            className="wd-btn wd-btn-gold mt-12"
                            data-testid="card-rsvp-scroll-btn"
                        >
                            {lang === "ar" ? "اضغط لتأكيد الحضور" : t("submit")}
                            <ChevronDown size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
